import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NuDsContext } from '@nubank/nuds-web/components/NuDSProvider/NuDSProvider';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Link from '@nubank/nuds-web/components/Link/Link';
import Box from '@nubank/nuds-web/components/Box/Box';
import NuLogo from '@nubank/nuds-web/components/NuLogo/NuLogo';
import dynamic from 'next/dynamic';

import Experiment from '@nubank/www-latam-commons/components/Experiment/Experiment';
import OpenFormSection from 'patterns/OpenFormSection/OpenFormSection';
import ShortForm from 'patterns/ShortForm/ShortForm';
import useOpenFormVisibility from 'utils/hooks/useOpenFormVisibility/useOpenFormVisibility';
import { smoothScroll as handleLinkClick } from 'utils/domUtils';
import { ScrollIndicator } from 'patterns/ScrollIndicator/ScrollIndicator';
import { PROGRESSIVE_DISCLOSURE_FORM_V2 } from 'utils/experiments';

import PixLogo from './components/PixLogo/PixLogo';

const ProgressiveDisclosureForm = dynamic(import('patterns/ProgressiveDisclosureForm/ProgressiveDisclosureForm'), { ssr: false });

const Arrow = () => (
  <svg width="62" height="62" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="path-1-outside-1" maskUnits="userSpaceOnUse" x="-30.9657" y="-26.2902" width="119.501" height="119.501" fill="black">
      <rect fill="white" x="-30.9657" y="-26.2902" width="119.501" height="119.501" />
      <path fillRule="evenodd" clipRule="evenodd" d="M16.5778 51.6316L60.3818 51.6316L60.3818 59.8461L10.3387 59.8461L10.3387 59.8942L2.12414 59.8942L2.12414 1.64601L10.3386 1.64601L10.3386 45.1976L52.6541 2.88224L58.9907 9.2188L16.5778 51.6316Z" />
    </mask>
    <path d="M16.5778 51.6316L15.5171 50.571L12.9564 53.1316L16.5778 53.1316L16.5778 51.6316ZM60.3818 51.6316L61.8818 51.6316L61.8818 50.1316L60.3818 50.1316L60.3818 51.6316ZM60.3818 59.8461L60.3818 61.3461L61.8818 61.3461L61.8818 59.8461L60.3818 59.8461ZM10.3387 59.8461L10.3387 58.3461L8.83865 58.3461L8.83865 59.8461L10.3387 59.8461ZM10.3387 59.8942L10.3387 61.3942L11.8387 61.3942L11.8387 59.8942L10.3387 59.8942ZM2.12414 59.8942L0.624132 59.8942L0.624132 61.3942L2.12414 61.3942L2.12414 59.8942ZM2.12414 1.64601L2.12414 0.146011L0.624132 0.146011L0.624131 1.64601L2.12414 1.64601ZM10.3386 1.64601L11.8387 1.64602L11.8387 0.146016L10.3387 0.146014L10.3386 1.64601ZM10.3386 45.1976L8.83865 45.1976L8.83865 48.8189L11.3993 46.2582L10.3386 45.1976ZM52.6541 2.88224L53.7148 1.82158L52.6541 0.760919L51.5935 1.82158L52.6541 2.88224ZM58.9907 9.2188L60.0514 10.2795L61.112 9.2188L60.0514 8.15815L58.9907 9.2188ZM16.5778 53.1316L60.3818 53.1316L60.3818 50.1316L16.5778 50.1316L16.5778 53.1316ZM58.8818 51.6316L58.8818 59.8461L61.8818 59.8461L61.8818 51.6316L58.8818 51.6316ZM60.3818 58.3461L10.3387 58.3461L10.3387 61.3461L60.3818 61.3461L60.3818 58.3461ZM11.8387 59.8942L11.8387 59.8461L8.83865 59.8461L8.83865 59.8942L11.8387 59.8942ZM2.12414 61.3942L10.3387 61.3942L10.3387 58.3942L2.12414 58.3942L2.12414 61.3942ZM0.624131 1.64601L0.624132 59.8942L3.62414 59.8942L3.62414 1.64601L0.624131 1.64601ZM10.3387 0.146014L2.12414 0.146011L2.12414 3.14601L10.3386 3.14601L10.3387 0.146014ZM11.8387 45.1976L11.8387 1.64602L8.83865 1.64601L8.83865 45.1976L11.8387 45.1976ZM51.5935 1.82158L9.27799 44.1369L11.3993 46.2582L53.7148 3.94289L51.5935 1.82158ZM60.0514 8.15815L53.7148 1.82158L51.5935 3.94289L57.93 10.2795L60.0514 8.15815ZM17.6384 52.6923L60.0514 10.2795L57.93 8.15815L15.5171 50.571L17.6384 52.6923Z" fill="white" mask="url(#path-1-outside-1)" />
  </svg>
);

function SectionForm({ onFormVisibilityChange }) {
  const { formatMessage } = useContext(NuDsContext);
  const [ref, isFormHidden] = useOpenFormVisibility();

  useEffect(() => {
    onFormVisibilityChange(isFormHidden);
  }, [isFormHidden]);

  return (
    <OpenFormSection ref={ref}>
      <Box
        display="grid"
        position="relative"
        padding={{ xs: '6x', md: '12x', lg: 56 }}
        minHeight={{ md: 'calc(100vh - 4rem)', lg: 'calc(100vh - 3.5rem)' }}
        gridTemplateColumns={{ md: 'repeat(6,1fr)', lg: 'repeat(12,1fr)' }}
        gridColumnGap={{ xs: '4x', md: '8x' }}
        gridTemplateAreas={{
          md: `
            "heading heading heading . . ."
            ". . arrowup form form form"
          `,
          lg: `
            ". . . . . . . . arrowdown . . ."
            "heading heading heading heading . . . . form form form form"
            ". . . arrowup . . . . . . . ."
          `,
        }}
        alignItems={{ lg: 'center' }}
        backgroundColor="primary.default"
        backgroundSize="cover"
        backgroundImage={{
          xs: {
            '1x': 'man-looking-up-smiling/man-looking-up-smiling-xs@1x.jpg',
            '2x': 'man-looking-up-smiling/man-looking-up-smiling-xs@2x.jpg',
            '3x': 'man-looking-up-smiling/man-looking-up-smiling-xs@3x.jpg',
          },
          md: {
            '1x': 'man-looking-up-smiling/man-looking-up-smiling-md@1x.jpg',
            '2x': 'man-looking-up-smiling/man-looking-up-smiling-md@2x.jpg',
            '3x': 'man-looking-up-smiling/man-looking-up-smiling-md@3x.jpg',
          },
          lg: {
            '1x': 'man-looking-up-smiling/man-looking-up-smiling-lg@1x.jpg',
            '2x': 'man-looking-up-smiling/man-looking-up-smiling-lg@2x.jpg',
            '3x': 'man-looking-up-smiling/man-looking-up-smiling-lg@3x.jpg',
          },
        }}
      >
        <Box
          minHeight={{
            xs: 'calc(100vh - 12rem)',
            md: 'auto',
          }}
          gridArea={{ md: 'heading' }}
        >
          <Box
            display="grid"
            gridTemplateColumns="auto 1fr"
            gridColumnGap="2x"
            marginBottom={{ xs: '4x', md: '6x' }}
          >
            <NuLogo
              size="small"
            />
            <PixLogo variant="white" />
          </Box>
          <Typography
            color="white"
            variant="title"
            intlKey="cms.pixPage.pixHeroTitle"
            gutterBottom={1.5}
          />

          <Typography
            color="white"
            variant="subtitle"
            intlKey="cms.pixPage.pixHeroCtaDescription"
            gutterBottom={1.5}
          />

          <Link
            onClick={handleLinkClick}
            variant="action"
            href={formatMessage({ id: 'cms.pixPage.pixHeroCtaLink' })}
            color="white"
            colorVariant="dark"
            iconProps={{ name: 'arrow-down' }}
            intlKey="cms.pixPage.pixHeroCtaText"
          />
        </Box>

        <Box
          gridArea={{ md: 'form' }}
          justifySelf={{ md: 'end' }}
        >
          <Experiment id={PROGRESSIVE_DISCLOSURE_FORM_V2.id}>
            <Experiment.Original>
              <ShortForm />
            </Experiment.Original>
            <Experiment.Variant id={PROGRESSIVE_DISCLOSURE_FORM_V2.emailsFieldsForm}>
              <ProgressiveDisclosureForm completeFormName="completeFormB" />
            </Experiment.Variant>
            <Experiment.Variant
              id={PROGRESSIVE_DISCLOSURE_FORM_V2.emailFieldConfirmationStepForm}
            >
              <ProgressiveDisclosureForm completeFormName="completeFormC" />
            </Experiment.Variant>
          </Experiment>
        </Box>
        <Box
          gridArea="arrowdown"
          display={{ xs: 'none', lg: 'block' }}
        >
          <Arrow />
        </Box>
        <Box
          gridArea="arrowup"
          display={{ xs: 'none', md: 'block' }}
          alignSelf="flex-start"
          transform="rotate(180deg)"
        >
          <Arrow />
        </Box>
        <ScrollIndicator href="#saiba-mais" onClick={handleLinkClick} />
      </Box>
    </OpenFormSection>
  );
}

SectionForm.propTypes = {
  onFormVisibilityChange: PropTypes.func.isRequired,
};

export default React.memo(SectionForm);
